<template>
    <div>
        <radio-buttons
            list-heading="App.RadioButtonListHeader.SelectTopic"
            :radio-button-list="radioButtonList"
            :default-radio-button="selectedRadioButtonData.labelText"
            :sub-topic="subTopic"
            :select-by-url="true"
            @selected-radio-button="handleSelectedSubjectRadioButton"
            @selected-drop-down="handleSelectedSubjectDropDown"
            @radio-buttons-changed="handleRadioButtonsChanged"
        />
        <checkbox-list
            v-show="officeTypesSearchVisible"
            list-heading="App.Tieliikenne.CheckboxList.PalveluPisteenTyyppi"
            :checkbox-list="officeTypesCheckboxList"
            @checkbox-item="handleOfficeTypesCheckboxValue"
        />
        <area-lists
            v-show="showAreaSelection"
            list-heading="App.RadioButtonListHeader.ValitseAlue"
            @selected-radio-button="handleAreaSelectionRadioButton"
            @selected-list-value="handleAreaSelectionListValue"
        />
        <div
            v-show="showSearchAddress"
            class="search-components"
        >
            <search-address />
            <locate-me class="locate-me-wrapper" />
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Vue, Watch, Prop } from "vue-facing-decorator"
    import ApiService from "@/services/ApiService"
    import SearchAddress from "@/components/SearchAddress.vue"
    import LocateMeLink from "@/components/LocateMeLink.vue"
    import RadioButtonListWithDropdown from "@/components/RadioButtonListWithDropdown.vue"
    import RadioButtonModel from "@/models/radiobuttonmodel"
    import CheckboxList from "@/components/CheckboxList.vue"
    import CheckboxModel from "@/models/checkboxmodel"
    import DropDownListModel from "@/models/dropdownlistmodel"
    import MunicipalitiesAndProvinces from "@/components/MunicipalitiesAndProvinces.vue"
    import SearchResultModel from "@/models/searchresultmodel"
    import { store } from '@/store/store'

    @Component({
        components: {
            "radio-buttons": RadioButtonListWithDropdown,
            "checkbox-list": CheckboxList,
            "locate-me": LocateMeLink,
            "search-address": SearchAddress,
            "area-lists": MunicipalitiesAndProvinces
        }
    })
    export default class Tieliikenne extends Vue {
        @Prop({ type: String }) subTopic!: string

        showAreaSelection = false
        showSearchAddress = false
        apiService: ApiService = new ApiService(store.getters.baseHref)

        pageName: string = "tieliikenne"

        // DataPublishServices rajapintakutsun etuosa
        apiCallBasePart: string = "v1/TieLiikenne/"

        // TODO TPa: Guidetext?

        radioButtonList = this.radioButtonListValues

        // Palvelupiste checkboxin alustus
        officeTypesSearchVisible: boolean = true
        officeTypesCheckboxList: Array<CheckboxModel>
            = [new CheckboxModel("App.TieLiikenne.AjokorttiJaLupapalvelupisteet", "AjokorttiJaLupapalvelupiste", true),
               new CheckboxModel("App.TieLiikenne.AjoneuvojenRekisteröintitoimipaikat", "Rekisterointitoimipaikka", true)]

        // Valittu radiobutton tietoineen
        selectedRadioButtonData: RadioButtonModel = this.defaultRadioButton
        selectedSubjectRadioDropDown: string = "Bensiini"

        selectedOfficeTypesFilters: string[] = ["AjokorttiJaLupapalvelupiste", "Rekisterointitoimipaikka"]

        async activated() {
            store.dispatch("setCurrentPage", this.pageName)
            this.radioButtonList = this.radioButtonListValues
            this.selectedRadioButtonData = this.defaultRadioButton
            store.dispatch("setSelectedSubjectRadioButton", this.selectedRadioButtonData)
            // Tehdään apikutsu, jolla haetaan kartan data
            await this.setDataToLoad(this.selectedRadioButtonData)
        }

        deactivated() {
            this.radioButtonList = []
            store.dispatch("setSubTopic", null)
            store.dispatch("setCurrentPage", "")
            store.dispatch("setSelectedDropDownResourceKey", "")
            store.dispatch("setSelectedAreaRadioButton", "")
            store.dispatch("setSelectedMunicipality", "")
            store.dispatch("setSelectedProvince", "")
        }

        // Aihevalinta muuttunut
        async handleSelectedSubjectRadioButton(item: RadioButtonModel) {
            this.selectedRadioButtonData = item
            this.selectedSubjectRadioDropDown = item.selectedDropDownListValue || ""
            store.dispatch("setSelectedSubjectRadioButton", item)
            // Tehdään apikutsu, jolla haetaan kartan data
            await this.setDataToLoad(this.selectedRadioButtonData)
        }

        get defaultRadioButton(): RadioButtonModel {
            // Jos sivulle tullaan subTopic routen kautta, niin asetetaan default radiobutton value routen mukaisesti
            if (this.subTopic) {
                for (let i = 0; i < this.radioButtonList.length; i++) {
                    if (this.radioButtonList[i].radioValue === this.subTopic) {
                        return this.radioButtonList[i]
                    }
                }
            }
            return this.radioButtonList[0]
        }

        // Aihevalinnan yhteydessä olevan dropdownin arvo muuttunut
        async handleSelectedSubjectDropDown(item: string) {
            this.selectedSubjectRadioDropDown = item
            // Päivitetään data kartalle
            this.updateData(store.getters.rightSlot)
        }

        async handleRadioButtonsChanged(list: Array<RadioButtonModel>) {
            this.updateComponentVisibility(list[0].radioValue)
            // Päivitä defaultina valittu radio button
            this.selectedRadioButtonData = list[0]
        }

        get currentPage(): string {
            return store.getters.currentPage
        }

        get activeLang(): SearchResultModel {
            return store.getters.activeLang
        }

        @Watch("activeLang")
        async onActiveLang(): Promise<void> {
            // Kielivalinta muuttunut. Ladataan data uudestaan.
            if (this.currentPage !== this.pageName) {
                return
            }
            this.updateData(store.getters.rightSlot)
        }

        async handleOfficeTypesCheckboxValue(item: CheckboxModel) {
            // palvelupiste tyyppien filttereiden asettaminen
            this.setOfficeTypesFilters(item)
            this.setDataToLoad(this.selectedRadioButtonData)
        }

        async setOfficeTypesFilters(item: CheckboxModel) {
            const index = this.selectedOfficeTypesFilters.indexOf(item.checkboxValue)
            index === -1 ? this.selectedOfficeTypesFilters.push(item.checkboxValue) : this.selectedOfficeTypesFilters.splice(index, 1)
            // TODO: Mitäs käyttäjä poistaa kaikki täpät? tyhjennetäänkö kartta? vai pitäisikö checkboxlistissä olla ominaisuus jolla estetään viimeisen valinnan poistaminen?
        }

        handleAreaSelectionRadioButton(item: RadioButtonModel): void {
            // Kunta/Maakunta/Koko Suomi valinta vaihtunut
            store.dispatch("setSelectedAreaRadioButton", String(item))
            this.updateData(store.getters.rightSlot)
        }

        handleAreaSelectionListValue(item: string): void {
            // Valittu kunta tai maakunta dropdownista
            if (store.getters.selectedAreaRadioButton === "kunta") {
                store.dispatch("setSelectedMunicipality", item)
            }
            else if (store.getters.selectedAreaRadioButton === "maakunta") {
                store.dispatch("setSelectedProvince", item)
            }
        }

        updateComponentVisibility(radioValue: string) {
            this.showAreaSelection = false
            this.showSearchAddress = false
            this.officeTypesSearchVisible = false

            if (radioValue === "LupaJaRekisterointitoimipaikat") {
                this.officeTypesSearchVisible = true
                this.showSearchAddress = true
            }
            else if (radioValue === "Liikenneonnettomuudet"
                || radioValue === "LiikennekaytossaHenkiloAutot"
                || radioValue === "LiikennekaytossaHenkiloAutotKeskiIka"
                || radioValue === "EnsirekisteroidytHenkiloAutot"
                || radioValue === "EnsirekisteroidytHenkiloAutotPaastot"
                || radioValue === "MyonnetytTaksiluvat"
                || radioValue === "VoimassaOlevatAjokortit") {
                this.showAreaSelection = true
            }
        }

        async setDataToLoad(selectedRadio: RadioButtonModel): Promise<void> {
            if (selectedRadio !== this.selectedRadioButtonData) {
                return
            }

            if (this.currentPage !== this.pageName) {
                return
            }

            this.updateData(selectedRadio.radioValue)
        }

        async updateData(radioValue: string): Promise<void> {
            store.dispatch("setLoading", { name: "tieliikenne", value: true })
            // Asetetaan oikean slotin sisältö
            store.dispatch("setRightSlot", radioValue)

            const clearLocationMarker = store.getters.currentPage.toLowerCase() !== store.getters.dataUrlToLoad?.url?.split("/")[1]?.toLowerCase()

            this.updateComponentVisibility(radioValue)

            let filter: string = ""
            const tieliikenteenTilastotApiUrl = this.apiCallBasePart + "TieliikenteenTilastot/"
            if (radioValue === "LupaJaRekisterointitoimipaikat") {
                // rajapinnassa tyyppi muuttuja joka on string,
                // jos tyyppi tyhjä niin haetaan sekä Ajokortti Ja Lupapalvelupisteet että Rekisteröintitoimipaikat.
                // Tyyppi rajauksena voi olla  'AjokorttiJaLupapalvelupiste' tai 'Rekisterointitoimipaikka' (non case-sensitive)
                if (this.selectedOfficeTypesFilters.length === 0) { // jos mikään tyyppi ei ole valittuna niin lähetetään tyhjä arvo
                    filter = ""
                }
                else if (this.selectedOfficeTypesFilters.length === this.officeTypesCheckboxList.length) { // jos kaikki tyypit valittuna
                    filter = "kaikki"
                }
                else {
                    filter = this.selectedOfficeTypesFilters[0].toString() // otetaan valittu tyyppi rajausehdoksi
                }
                store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "LupaJaRekisterointitoimipaikat/" + radioValue + "?Tyyppi=" + filter, type: radioValue, clearLocationMarker })
            }
            else if (radioValue === "Liikenneonnettomuudet") {
                const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                if (selectedAreaRadioButton === "kunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "TieliikenneOnnettomuudetKuntaKohtainen", type: radioValue, clearLocationMarker })
                }
                else if (selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "TieliikenneOnnettomuudetMaakuntaKohtainen", type: radioValue, clearLocationMarker })
                }
                else {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "TieliikenneOnnettomuudetKokoMaa", type: radioValue, clearLocationMarker })
                }
            }
            else if (radioValue === "LiikennekaytossaHenkiloAutot") {
                const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                if (selectedAreaRadioButton === "kunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKuntaKohtainen?type=LiikennekaytossaHenkiloAutot&propulsionType=" + this.selectedSubjectRadioDropDown, type: radioValue, clearLocationMarker })
                }
                else if (selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotMaakuntaKohtainen?type=LiikennekaytossaHenkiloAutot&propulsionType=" + this.selectedSubjectRadioDropDown, type: radioValue, clearLocationMarker })
                }
                else {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKokoMaa?type=LiikennekaytossaHenkiloAutot", type: radioValue, clearLocationMarker })
                }
            }
            else if (radioValue === "LiikennekaytossaHenkiloAutotKeskiIka") {
                const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                if (selectedAreaRadioButton === "kunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKuntaKohtainen?type=LiikennekaytossaHenkiloAutotKeskiIka", type: radioValue, clearLocationMarker })
                }
                else if (selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotMaakuntaKohtainen?type=LiikennekaytossaHenkiloAutotKeskiIka", type: radioValue, clearLocationMarker })
                }
                else {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKokoMaa?type=LiikennekaytossaHenkiloAutotKeskiIka", type: radioValue, clearLocationMarker })
                }
            }
            else if (radioValue === "EnsirekisteroidytHenkiloAutot") {
                const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                if (selectedAreaRadioButton === "kunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKuntaKohtainen?type=EnsirekisteroidytHenkiloAutot&propulsionType=" + this.selectedSubjectRadioDropDown, type: radioValue, clearLocationMarker })
                }
                else if (selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotMaakuntaKohtainen?type=EnsirekisteroidytHenkiloAutot&propulsionType=" + this.selectedSubjectRadioDropDown, type: radioValue, clearLocationMarker })
                }
                else {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKokoMaa?type=EnsirekisteroidytHenkiloAutot", type: radioValue, clearLocationMarker })
                }
            }
            else if (radioValue === "EnsirekisteroidytHenkiloAutotPaastot") {
                const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                if (selectedAreaRadioButton === "kunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKuntaKohtainen?type=EnsirekisteroidytHenkiloAutotPaastot", type: radioValue, clearLocationMarker })
                }
                else if (selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotMaakuntaKohtainen?type=EnsirekisteroidytHenkiloAutotPaastot", type: radioValue, clearLocationMarker })
                }
                else {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKokoMaa?type=EnsirekisteroidytHenkiloAutotPaastot", type: radioValue, clearLocationMarker })
                }
            }
            else if (radioValue === "MyonnetytTaksiluvat") {
                const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                if (selectedAreaRadioButton === "kunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKuntaKohtainen?type=MyonnetytTaksiluvat", type: radioValue, clearLocationMarker })
                }
                else if (selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotMaakuntaKohtainen?type=MyonnetytTaksiluvat", type: radioValue, clearLocationMarker })
                }
                else {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKokoMaa?type=MyonnetytTaksiluvat", type: radioValue, clearLocationMarker })
                }
            }
            else if (radioValue === "VoimassaOlevatAjokortit") {
                const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                if (selectedAreaRadioButton === "kunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKuntaKohtainen?type=VoimassaOlevatAjokortit", type: radioValue, clearLocationMarker })
                }
                else if (selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotMaakuntaKohtainen?type=VoimassaOlevatAjokortit", type: radioValue, clearLocationMarker })
                }
                else {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKokoMaa?type=VoimassaOlevatAjokortit", type: radioValue, clearLocationMarker })
                }
            }

            store.dispatch("setLoading", { name: "tieliikenne", value: false })
        }

        get leftSlotHeaderTieliikenne(): string | undefined {
            return this.$t("App.LeftSlot.Header.TieLiikenne")
        }

        // TODO TPa: tarkista radioValuet vs api, default value
        get radioButtonListValues(): Array<RadioButtonModel> {
            return [new RadioButtonModel("App.TieLiikenne.RadioList.LupaJaRekisterointiPaikat", "LupaJaRekisterointitoimipaikat"),
                    new RadioButtonModel("App.TieLiikenne.RadioList.EnsirekisteroidytHenkiloAutot", "EnsirekisteroidytHenkiloAutot", "", this.powers, "Bensiini"),
                    new RadioButtonModel("App.TieLiikenne.RadioList.EnsirekisteroidytHenkiloAutotPaastot", "EnsirekisteroidytHenkiloAutotPaastot"),
                    new RadioButtonModel("App.TieLiikenne.RadioList.LiikennekaytossaHenkiloAutot", "LiikennekaytossaHenkiloAutot", "", this.powers, "Bensiini"),
                    new RadioButtonModel("App.TieLiikenne.RadioList.LiikennekaytossaHenkiloAutotKeskiIka", "LiikennekaytossaHenkiloAutotKeskiIka"),
                    new RadioButtonModel("App.TieLiikenne.RadioList.Liikenneonnettomuudet", "Liikenneonnettomuudet", "", null, "0"),
                    new RadioButtonModel("App.TieLiikenne.RadioList.Taksiluvat", "MyonnetytTaksiluvat"),
                    new RadioButtonModel("App.TieLiikenne.RadioList.VoimassaOlevatAjokortit", "VoimassaOlevatAjokortit")
            ]
        }

        // TODO TPa: Identiefier?
        get powers(): DropDownListModel[] {
            return [new DropDownListModel("App.TieLiikenne.DropDown.Kayttovoimat.Bensiini", "Bensiini", false),
                    new DropDownListModel("App.TieLiikenne.DropDown.Kayttovoimat.Diesel", "Diesel", false),
                    new DropDownListModel("App.TieLiikenne.DropDown.Kayttovoimat.Kaasu", "Kaasu", false),
                    new DropDownListModel("App.TieLiikenne.DropDown.Kayttovoimat.LadattavaHybridi", "Ladattavat hybridit", false),
                    new DropDownListModel("App.TieLiikenne.DropDown.Kayttovoimat.Sahko", "Sähkö", false),
                    new DropDownListModel("App.TieLiikenne.DropDown.Kayttovoimat.Muut", "Muut", false)]
        }
    }
</script>
